import { useState, useEffect, useCallback, useRef } from "react";
import { X, Volume2, VolumeX, ArrowRight, Zap, Sparkles, Play, Pause } from "lucide-react";

// V165.2 — replaced the previous video asset with Mika's brand portrait +
// OpenAI TTS voiceover (generated server-side via /api/tts/exit-popup).
const PORTRAIT_URL = "https://static.prod-images.emergentagent.com/jobs/b798ee84-c0e9-45cd-8132-8a7a658b0230/images/5f745f6f9a5b7b709456a091b50dffc57c18888b52cacf158da597af6d561bca.png";
const TTS_URL = (lang) => `${process.env.REACT_APP_BACKEND_URL}/api/tts/exit-popup`;
const TTS_LANG_BODY = (lang) => ({ language: lang });

const COPY = {
  sk: {
    eyebrow: "POČKAJ ESTE CHVÍĽKU",
    h1: "Tvoj web stráca peniaze každý deň, ktorý nemá agenta.",
    sub: "Spusti bezplatný AI scan a uvidíš za 60 sekúnd ktorý agent ti najviac ušetrí čas.",
    cta_scan: "BEZPLATNÝ SCAN WEBU",
    cta_agents: "Pozri 14 agentov",
    sub_cta: "Žiadna karta · GDPR · EU · 14 dní zadarmo",
    audio_hint: "Klikni pre zvuk",
    close: "Zatvoriť",
  },
  cs: {
    eyebrow: "POČKEJ JEŠTĚ CHVILKU",
    h1: "Tvůj web ztrácí peníze každý den, kdy nemá agenta.",
    sub: "Spusť bezplatný AI scan a uvidíš za 60 sekund, který agent ti nejvíc ušetří čas.",
    cta_scan: "BEZPLATNÝ SCAN WEBU",
    cta_agents: "Prohlédnout 14 agentů",
    sub_cta: "Bez karty · GDPR · EU · 14 dní zdarma",
    audio_hint: "Klikni pro zvuk",
    close: "Zavřít",
  },
  en: {
    eyebrow: "WAIT A MOMENT",
    h1: "Your site loses money every day without an AI agent.",
    sub: "Run a free scan and see in 60 seconds which agent saves you the most time.",
    cta_scan: "FREE WEBSITE SCAN",
    cta_agents: "See 14 agents",
    sub_cta: "No card · GDPR · EU · 14 days free",
    audio_hint: "Click for sound",
    close: "Close",
  },
  de: {
    eyebrow: "WARTE EINEN MOMENT",
    h1: "Deine Seite verliert Geld an jedem Tag ohne AI-Agent.",
    sub: "Starte einen kostenlosen Scan und sieh in 60 Sekunden, welcher Agent dir am meisten Zeit spart.",
    cta_scan: "KOSTENLOSER SCAN",
    cta_agents: "14 Agenten ansehen",
    sub_cta: "Keine Karte · DSGVO · EU · 14 Tage gratis",
    audio_hint: "Klick für Ton",
    close: "Schließen",
  },
  pl: {
    eyebrow: "POCZEKAJ CHWILĘ",
    h1: "Twoja strona traci pieniądze każdego dnia bez agenta AI.",
    sub: "Uruchom darmowy scan i zobacz w 60 sekund, który agent oszczędzi ci najwięcej czasu.",
    cta_scan: "DARMOWY SCAN STRONY",
    cta_agents: "Zobacz 14 agentów",
    sub_cta: "Bez karty · RODO · UE · 14 dni gratis",
    audio_hint: "Kliknij dla dźwięku",
    close: "Zamknij",
  },
  hu: {
    eyebrow: "VÁRJ EGY KICSIT",
    h1: "Az oldalad minden nap pénzt veszít AI ügynök nélkül.",
    sub: "Indíts ingyenes scan-t és nézd meg 60 másodperc alatt, melyik ügynök spórol neked legtöbb időt.",
    cta_scan: "INGYENES WEB SCAN",
    cta_agents: "14 ügynök megtekintése",
    sub_cta: "Kártya nélkül · GDPR · EU · 14 nap ingyen",
    audio_hint: "Kattints a hangért",
    close: "Bezárás",
  },
};

const SHOW_AFTER_MS = 15000;
const STORAGE_KEY = "trp_exit_video_shown_at";
// V218.2 — once per 24 hours instead of once per session. Closing the
// tab and reopening the homepage used to re-show the popup immediately,
// which felt spammy. localStorage + timestamp gates it properly.
const COOLDOWN_MS = 24 * 60 * 60 * 1000;

function _alreadyShownToday() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!ts || Number.isNaN(ts)) return false;
    return Date.now() - ts < COOLDOWN_MS;
  } catch {
    return false;
  }
}

function _markShown() {
  try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
}

export function ExitIntentVideoPopup({ language = "sk" }) {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const armedRef = useRef(false);
  const lastTouchY = useRef(0);

  // V165.2 — broadcast a global "modal is open" flag so siblings like
  // AIConciergeWidget can step out of the way. Avoids the bottom-right
  // pile-up reported by the owner.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (show) {
      window.__trp_modal_open = true;
      try { window.dispatchEvent(new CustomEvent("trp:modal", { detail: { open: true } })); } catch {}
    } else {
      window.__trp_modal_open = false;
      try { window.dispatchEvent(new CustomEvent("trp:modal", { detail: { open: false } })); } catch {}
    }
  }, [show]);

  const trigger = useCallback(() => {
    if (dismissed) return;
    if (_alreadyShownToday()) return;
    if (!armedRef.current) return;
    setShow(true);
    _markShown();
  }, [dismissed]);

  // V165.2 — TTS audio loader. Browsers block audio autoplay (no muted
  // audio trick available like with video), so we only PRELOAD when popup
  // opens, then wait for the user to click the speaker. POST the language
  // to /api/tts/exit-popup which returns cached mp3 bytes.
  useEffect(() => {
    if (!show) return undefined;
    if (!audioRef.current) return undefined;
    const a = audioRef.current;
    a.preload = "auto";
    // Try optimistic autoplay (will fail unless user has previously
    // interacted with the document). The Play button below is the fallback.
    const tryPlay = () => {
      const p = a.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => { /* user must click Play */ });
      }
    };
    a.addEventListener("canplaythrough", tryPlay, { once: true });
    return () => a.removeEventListener("canplaythrough", tryPlay);
  }, [show]);

  // Arm the trigger only AFTER user has been on page 15s (avoid annoying
  // bounce-back immediately after landing — exit-intent works best on
  // engaged visitors).
  useEffect(() => {
    if (_alreadyShownToday()) return;
    const t = setTimeout(() => { armedRef.current = true; }, SHOW_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  // Desktop: mouseleave from top edge
  useEffect(() => {
    const onLeave = (e) => {
      if (e.clientY <= 5) trigger();
    };
    document.addEventListener("mouseleave", onLeave);
    return () => document.removeEventListener("mouseleave", onLeave);
  }, [trigger]);

  // Mobile: rapid swipe-up (scroll-up velocity) or back gesture proxy
  useEffect(() => {
    if (window.innerWidth >= 768) return;
    const onTouchStart = (e) => { lastTouchY.current = e.touches[0].clientY; };
    const onTouchMove = (e) => {
      const dy = e.touches[0].clientY - lastTouchY.current;
      // fast pull-down at top of page = "I'm leaving" signal
      if (window.scrollY < 30 && dy > 80) trigger();
    };
    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
    };
  }, [trigger]);

  // Esc to close
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handleClose = () => {
    setShow(false);
    setDismissed(true);
  };

  const goScan = () => {
    setShow(false);
    setDismissed(true);
    window.location.href = "/agent-match";
  };

  const goAgents = () => {
    setShow(false);
    setDismissed(true);
    window.location.href = "/prenajmi-si-agenta";
  };

  const togglePlay = async () => {
    if (!audioRef.current) return;
    const a = audioRef.current;
    if (a.paused) {
      try {
        await a.play();
        setPlaying(true);
      } catch (_) { /* play() rejected — browser autoplay block */ }
    } else {
      a.pause();
      setPlaying(false);
    }
  };

  const t = COPY[language] || COPY.sk;

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 animate-in fade-in duration-200"
      data-testid="exit-video-popup"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-md"
        onClick={handleClose}
        data-testid="exit-video-backdrop"
      />

      {/* Card */}
      <div
        className="relative bg-[#0a0a0a] border border-[#F59E0B] max-w-2xl w-full shadow-2xl shadow-amber-900/40 animate-in zoom-in-95 duration-200"
        data-testid="exit-video-content"
      >
        {/* Close */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-20 p-2 bg-black/60 hover:bg-black/80 text-white/70 hover:text-white rounded-full transition-colors"
          aria-label={t.close}
          data-testid="exit-video-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Portrait + voice-over block (V165.2 — was video) */}
        <div className="relative aspect-[16/10] bg-black overflow-hidden">
          <img
            src={PORTRAIT_URL}
            alt="Mika — Agent OS"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="exit-popup-portrait"
            loading="eager"
          />
          {/* Hidden audio element — TTS mp3 from /api/tts/exit-popup */}
          <audio
            ref={audioRef}
            src={`${process.env.REACT_APP_BACKEND_URL}/api/tts/exit-popup-stream?language=${language}`}
            onEnded={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            data-testid="exit-popup-audio"
            preload="auto"
          />
          {/* Play / Pause toggle for the voice-over */}
          <button
            onClick={togglePlay}
            className="absolute bottom-3 left-3 z-10 flex items-center gap-2 px-3 py-2 bg-black/70 backdrop-blur-sm hover:bg-black/90 text-white text-xs font-bold tracking-wider transition-colors"
            data-testid="exit-popup-play-toggle"
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            <span className="uppercase tracking-widest">{playing ? "🔊" : t.audio_hint}</span>
          </button>
          {/* Brand watermark */}
          <span className="absolute top-3 left-3 z-10 text-[10px] tracking-[0.3em] font-black uppercase text-amber-400 bg-black/60 backdrop-blur-sm px-2 py-1">
            Mika · Agent OS
          </span>
          {/* Gradient overlay */}
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent pointer-events-none" />
        </div>

        {/* Copy + CTAs */}
        <div className="p-6 sm:p-8">
          <p
            className="text-[10px] sm:text-[11px] tracking-[0.3em] text-[#F59E0B] uppercase font-black mb-2 flex items-center gap-2"
            data-testid="exit-video-eyebrow"
          >
            <Sparkles className="w-3 h-3" />
            {t.eyebrow}
          </p>
          <h2
            className="text-2xl sm:text-3xl font-black leading-tight text-white mb-3"
            data-testid="exit-video-headline"
          >
            {t.h1}
          </h2>
          <p className="text-sm sm:text-base text-zinc-400 mb-6 leading-relaxed">
            {t.sub}
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={goScan}
              className="flex-1 group flex items-center justify-center gap-2 px-6 py-4 bg-[#F59E0B] hover:bg-[#fbbf24] text-black font-black text-sm sm:text-base uppercase tracking-widest transition-all shadow-[0_0_30px_rgba(245,158,11,0.4)] hover:shadow-[0_0_50px_rgba(245,158,11,0.6)]"
              data-testid="exit-video-cta-scan"
            >
              <Zap className="w-5 h-5" />
              {t.cta_scan}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={goAgents}
              className="flex-1 px-6 py-4 bg-transparent border-2 border-zinc-700 hover:border-[#F59E0B] text-white font-bold text-sm uppercase tracking-wider transition-colors"
              data-testid="exit-video-cta-agents"
            >
              {t.cta_agents} →
            </button>
          </div>

          <p
            className="text-[11px] text-zinc-600 text-center mt-4 tracking-wider"
            data-testid="exit-video-sub-cta"
          >
            {t.sub_cta}
          </p>
        </div>
      </div>
    </div>
  );
}

export default ExitIntentVideoPopup;
